import React, { useEffect, useState } from "react";
import "../../Assets/Styles/pages/landingRedesign.scss";
import "../../Assets/Styles/pages/blogsPageRedesign.scss";
import Nav from "../../Components/LandingComponents/Nav";
import BlogsHero from "../../Components/BlogsPageComponents/BlogsHero";
import BlogsFeatured from "../../Components/BlogsPageComponents/BlogsFeatured";
import BlogsGrid from "../../Components/BlogsPageComponents/BlogsGrid";
import BlogsContactCTA from "../../Components/BlogsPageComponents/BlogsContactCTA";
import { BLOG_POSTS } from "../../Config/blogPosts";
import type { DisplayPost } from "../../Components/BlogsPageComponents/blogTypes";
import usePageMetadata from "../../Utils/usePageMetadata";

// The global Footer still renders below every route including this one. The global Header's
// desktop bar is suppressed for "/blogs" in Header.tsx's `condition()` -- Nav replaces it,
// same pattern as the other redesigned marketing pages.
//
// Source priority: our own Blogs API (`GET {REACT_APP_API_URL}blogs`) first, then the Ghost CMS
// feed (same endpoint the old BlogsList used) as a second fallback, then the local static content
// in Config/blogPosts.ts if both are empty/unreachable -- true right now for Ghost, the CMS has
// no posts -- instead of rendering an empty page. Once either upstream has real posts, this
// switches to them automatically with no code change needed.
//
// Blogs API response shape (confirmed against the real deployed API, not the integration doc --
// that doc had no sample body):
//   { blogs: [{ id, title, slug, content, summary, coverImage, category, tags, status, author,
//               seoTitle, seoDescription, createdAt, updatedAt, deletedAt }], total, page, limit }
// The server already filters to `status: "published"` only, per the doc's own description --
// no client-side status filtering needed here. `summary` is the excerpt field (often empty in
// practice; the cards degrade cleanly to no excerpt text when so, same as an empty Ghost excerpt).
interface BlogsApiPost {
  id: string;
  title: string;
  slug: string;
  summary?: string;
  coverImage?: string;
  category?: string;
  createdAt?: string;
}
interface BlogsApiResponse {
  blogs: BlogsApiPost[];
  total: number;
  page: number;
  limit: number;
}

const Blogs: React.FC = () => {
  // `posts` starts empty (NOT `BLOG_POSTS`) -- the static content is a genuine fallback for when
  // every real source fails, not a placeholder to show while they're still loading. Showing it
  // first and then swapping to real posts a moment later read as "wrong blogs flash on screen",
  // not as loading. `isLoading` drives a skeleton instead; static content only appears if the
  // fetch chain actually finishes with nothing.
  const [posts, setPosts] = useState<DisplayPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const ghostApiUrl = process.env.REACT_APP_GHOST_API_URL;
  const ghostApiKey = process.env.REACT_APP_CONTENT_API_KEY;

  usePageMetadata(
    "Blogs & News - eZy WaterMark",
    "Read the latest blogs and news about image branding, photography tips, and watermarking strategies on eZy Watermark."
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      // Try our own Blogs API first. Public/no-auth per its integration doc -- verified purely by
      // request Origin/Referer server-side, so this is a plain `fetch`, not the shared axios
      // instance (which always attaches a Firebase Authorization header and awaits auth-state
      // restore -- unnecessary delay/coupling for a public marketing page's GET).
      if (apiBaseUrl) {
        try {
          const base = apiBaseUrl.endsWith("/") ? apiBaseUrl : `${apiBaseUrl}/`;
          const response = await fetch(`${base}blogs?page=1&limit=20`);
          if (response.ok) {
            const raw = (await response.json()) as BlogsApiResponse;
            if (Array.isArray(raw.blogs) && raw.blogs.length > 0) {
              setPosts(
                raw.blogs.map((b) => ({
                  slug: b.slug,
                  title: b.title,
                  excerpt: b.summary ?? "",
                  cover: b.coverImage ?? "",
                  date: b.createdAt,
                  category: b.category,
                }))
              );
              setIsLoading(false);
              return;
            }
            // else: no published blogs yet -- fall through to Ghost/static below.
          } else {
            console.error("[BlogsAPI] request failed with status", response.status);
          }
        } catch (error) {
          console.error("[BlogsAPI] Error:", error);
        }
      }

      // Fall back to the Ghost CMS feed.
      if (!ghostApiUrl || !ghostApiKey) return;
      try {
        const response = await fetch(`${ghostApiUrl}/ghost/api/content/posts/?key=${ghostApiKey}`, {
          headers: { "Accept-Version": "v5.69" },
        });

        if (response.ok) {
          const blogsList = (await response.json()) as {
            posts?: Array<{
              title: string;
              slug: string;
              feature_image?: string;
              excerpt?: string;
              published_at?: string;
            }>;
          };
          if (blogsList.posts && blogsList.posts.length > 0) {
            setPosts(
              blogsList.posts.map((blog) => ({
                slug: blog.slug,
                title: blog.title,
                excerpt: blog.excerpt ?? "",
                cover: blog.feature_image ?? "",
                date: blog.published_at,
              }))
            );
            setIsLoading(false);
            return;
          }
          // else: fall through to the static fallback below.
        } else {
          console.error("Error: Unable to fetch blogs data");
        }
      } catch (error) {
        console.error("Error:", error);
      }

      // Every real source came back empty/unreachable -- this is the genuine fallback case.
      setPosts(BLOG_POSTS);
      setIsLoading(false);
    };
    void fetchBlogs();
  }, [apiBaseUrl, ghostApiUrl, ghostApiKey]);

  const featured = posts.find((p) => p.featured) ?? posts[0];
  const rest = posts.filter((p) => p !== featured);

  return (
    <div className="landing-redesign">
      <Nav />
      <BlogsHero />
      {isLoading ? <BlogsSkeleton /> : (
        <>
          {featured && <BlogsFeatured post={featured} />}
          <BlogsGrid posts={rest} />
        </>
      )}
      <BlogsContactCTA />
    </div>
  );
};

/**
 * Shown while the Blogs API (and its Ghost/static fallbacks) are still resolving. Reuses the
 * real `.bp-featured`/`.bp-card` layout classes (see blogsPageRedesign.scss) so nothing jumps in
 * size once real content swaps in -- only the shimmer blocks are skeleton-specific.
 */
const BlogsSkeleton: React.FC = () => (
  <>
    <section className="bp-featured" aria-hidden="true">
      <div className="bp-featured__card bp-skeleton-card">
        <div className="bp-featured__media bp-skeleton-shimmer" />
        <div className="bp-featured__body">
          <div className="bp-skeleton-shimmer bp-skeleton-line" style={{ width: "30%" }} />
          <div className="bp-skeleton-shimmer bp-skeleton-line" style={{ width: "80%", height: "1.6em", marginTop: "0.75em" }} />
          <div className="bp-skeleton-shimmer bp-skeleton-line" style={{ width: "95%", marginTop: "1em" }} />
          <div className="bp-skeleton-shimmer bp-skeleton-line" style={{ width: "70%", marginTop: "0.5em" }} />
        </div>
      </div>
    </section>
    <section className="bp-grid" aria-hidden="true">
      <div className="bp-grid__list">
        {[0, 1, 2].map((i) => (
          <div key={i} className="bp-card bp-skeleton-card">
            <div className="bp-card__media bp-skeleton-shimmer" />
            <div className="bp-card__body">
              <div className="bp-skeleton-shimmer bp-skeleton-line" style={{ width: "40%" }} />
              <div className="bp-skeleton-shimmer bp-skeleton-line" style={{ width: "90%", height: "1.2em", marginTop: "0.6em" }} />
              <div className="bp-skeleton-shimmer bp-skeleton-line" style={{ width: "60%", marginTop: "0.6em" }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  </>
);

export default Blogs;
